import { Button, Flex, Modal, NumberFormatter, Stack, Title } from "@mantine/core";
import { IMinUserDepricated, IUserDepricated } from "@mctypes/global.types";
import { IBoardBucket, IIssueBoardApi, IMapOfEvaluationCategoriesApi, IRisikoProbability } from "@mcapps/shared";
import { IIssueApi, IUserPartial } from "@schemas/issueSchema";
import { IOtherAttributeConfigApi } from "@schemas/otherAttributesSchema";
import { IProjectMemberApiItem } from "@schemas/projectSchema";
import { IconPlus } from "@tabler/icons-react";
import {
  CellEditRequestEvent,
  ColDef,
  ColDefField,
  ICellRendererParams,
  SideBarDef,
  ValueFormatterParams,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "ag-grid-enterprise";
import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import * as R from "ramda";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useGetProjectByProjectIdConnectedTasksQuery,
  usePutProjectByProjectIdIssueConnectedTasksUpdateOrderMutation,
  usePutProjectByProjectIdIssueUpdateSimpleMutation,
} from "../../../../../../openapi";
import { AG_GRID_LOCALE_NOR } from "../../../../../agGridLocale";
import { Avatar, CopyIssueButton, DisplayDueDate, FailedAlert, OptionBadge, Tags } from "../../../../../components";
import { UserCellRenderer } from "../../../../../components/ConnectedIssues/ConnectedIssues";
import { McMantineMultiUserSelect } from "../../../../../components/McMantineUserSelect/McMantineUserMultiSelect";
import SimpleStampCheck from "../../../../../components/SimpleStampCheck/SimpleStampCheck";
import { CellEditFormatter } from "../../../../../components/columnGlobal";
import { setShowFailedMessageThunk } from "../../../../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";
import { getMatrixItemAndScore } from "../../../../../mc-shared/sharedUtils";
import LocalErrorBoundary from "../../../../../util/LocalErrorBoundary";
import { debounce, formatNumberNb } from "../../../../utils";
import { formatShortDate, isValidDateValue, parseNorwegianDate } from "../../../admin/selectors";
import { IssueIdWithAksjonCompletedCount } from "../../IssueIdWithAksjonCompletedCount";
import { doFetchUniqIssueTagsOnBoardDepricated } from "../../issueAPIDepricated";
import {
  DEFAULT_VISIBLE_ISSUE_COLUMNS,
  ISSUE_COLUMN_LABELS,
  ISSUE_COMPLETED_VALUE,
  ISSUE_DATAFILEDS_ENUM,
  ISSUE_EXPIRED,
  ISSUE_PRIORTY_OPTIONS,
  ISSUE_SECTIONS,
  ISSUE_STATES_OPTIONS,
} from "../../issueConstants";
import { useCreateIssueMutation, useDoUpdateIssueMutation } from "../../issueRTK";
import { selectActiveTableTemplate } from "../../issueSelectors";
import { setIssueSortedColInRedux, updateColumnStateThunk } from "../../issueSlice";
import {
  convertValueAndColorToMultiDisplayColor,
  getIssueActionNameOption,
  issueBoardHasClientAccess,
  useDragColumnChange,
} from "../../issueUtils";
import { selectClickedRoomNumberInModal } from "../Floorplan/floorplanSelectors";
import { setClickedRoomNumberInModalInRedux } from "../Floorplan/floorplanSlice";
import { IssueConnectedBimSpacesSection } from "../IssueItemModal/new/IssueConnectedBimSpacesSection";
import IssueModalRoomViewer from "../IssueItemModal/new/IssueModalRoomViewer";
import ConnectedTaskDeleteCell from "./ConnectedTaskDeleteCell";
import ConnectedTasksAndIssuesMiniTable from "./ConnectedTasksAndIssuesMiniTable";

interface IIssueTableAgGridColumnProps {
  boardBuckets: IBoardBucket[];
  setSelectedIssue: (issue: IIssueApi) => void;
  types: string[];
  pnsItems: any[];
  members: IProjectMemberApiItem[];
  issueBoardId: string;
  projectId: string;
  updateItemMembers: (issueId: string, itemMembers: IMinUserDepricated[]) => void;
  otherAttributesConfig: IOtherAttributeConfigApi;
  otherAttributesTasksConfig?: IOtherAttributeConfigApi;
  issueBoard: IIssueBoardApi;
}

export const dateExportFormatter = (params: ValueFormatterParams) => {
  if (params.value == null || params.value === "") {
    return "";
  }
  const date = new Date(params.value);
  if (isNaN(date.getTime())) {
    return "";
  }
  return formatShortDate(date, true);
};

const getLogValueFromCellValue = (value: unknown) => {
  if (value == null) {
    return "";
  }

  if (typeof value === "object" && "name" in value) {
    return (value as { name?: string }).name ?? "";
  }

  return value;
};

const IssueTableAgGrid: React.FC<{
  items: IIssueApi[];
  columnProps: IIssueTableAgGridColumnProps;
  loading: boolean;
  gridRef: React.RefObject<AgGridReact>;
}> = ({ items, columnProps, gridRef }) => {
  const {
    boardBuckets,
    setSelectedIssue,
    types,
    pnsItems,
    members,
    issueBoardId,
    projectId,
    updateItemMembers,
    otherAttributesConfig,
    otherAttributesTasksConfig,
    issueBoard,
  } = columnProps;
  let refContainer = useRef(null);
  const allColumnState = useAppSelector((state) => state.issueReducerNew.allColumnState);
  const activeTableTemplate = useAppSelector(selectActiveTableTemplate);
  const clickedRoomNumberInModal = useAppSelector(selectClickedRoomNumberInModal);
  const [updateIssue] = useDoUpdateIssueMutation();
  const [updateIssueSimple] = usePutProjectByProjectIdIssueUpdateSimpleMutation();
  const [addNewIssue, { isLoading }] = useCreateIssueMutation();
  const [reorderConnectedTasks] = usePutProjectByProjectIdIssueConnectedTasksUpdateOrderMutation();
  const dispatch = useAppDispatch();
  const boardHasClientAccess = issueBoardHasClientAccess(issueBoard);
  const columnsVisibility =
    (allColumnState[issueBoardId]?.length > 0
      ? allColumnState[issueBoardId]?.map((col) => col.colId)
      : DEFAULT_VISIBLE_ISSUE_COLUMNS) ?? DEFAULT_VISIBLE_ISSUE_COLUMNS; // use default cols if template is empty, or no state
  const hideAndSort = (colId: string): { hide: boolean; sort: "asc" | "desc"; type: string } => {
    const sortedCol = activeTableTemplate?.sortedCol;
    return {
      hide: !columnsVisibility.includes(colId),
      sort: sortedCol?.column === colId ? (sortedCol.direction.toLowerCase() as "asc" | "desc") : null,
      type: issueBoard.disabledColumns?.includes(colId) ? "alwaysHidden" : "default",
    };
  };

  const closeRoomViewerModal = useCallback(() => {
    dispatch(setClickedRoomNumberInModalInRedux({ issueBoardId, clickedRoomNumberInModal: null }));
  }, [dispatch, issueBoardId]);

  useEffect(() => {
    return () => {
      dispatch(setClickedRoomNumberInModalInRedux({ issueBoardId, clickedRoomNumberInModal: null }));
    };
  }, [dispatch, issueBoardId]);

  const getAgGridIssueColTextAndLabel = (
    dataField: ISSUE_DATAFILEDS_ENUM,
  ): { field: string; headerName: string; hide: boolean; sort: "asc" | "desc" } => {
    return {
      field: dataField,
      headerName: ISSUE_COLUMN_LABELS[dataField],
      ...hideAndSort(dataField),
    };
  };

  const agDateCellEditor = (dateType: string) => {
    return {
      editable: true,
      context: ISSUE_SECTIONS.DATES,
      cellEditor: "agDateCellEditor",
      valueFormatter: dateExportFormatter,
      valueGetter: (params) => {
        if (params.data.dates && params.data.dates[dateType]) {
          return new Date(params.data.dates[dateType]);
        } else {
          return null;
        }
      },
    };
  };
  const agUserCellEditor = (userType: string) => {
    return {
      editable: true,
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      suppressPaste: true,
      valueFormatter: (params: ValueFormatterParams) => {
        return params.data[userType]?.name?.length > 0 ? params.data[userType]?.name : " ";
      },
      valueGetter: (params) => {
        return params.data[userType]?.name ?? null;
      },
      cellEditorParams: {
        values: [
          null,
          ...members
            .map((m) => m.user)
            .filter((user): user is IUserDepricated => user != null && typeof user !== "string"),
        ],
        formatValue: (value) => value?.name ?? "",
        allowTyping: true,
        filterList: true,
        cellRenderer: (params) => <UserCellRenderer height="15px" user={params.value} />,
      },
      filter: "agSetColumnFilter",
      sortable: true,
    } as ColDef<any>;
  };

  const InfoPanel = () => {
    return (
      <div className="p-2">
        <h4 className="mb-3">Tabelloversikt</h4>
        <h5>Justerbare kolonner:</h5>
        <p>
          Klikk på en kolonneoverskriftsdeler for å endre størrelsen på en kolonne. Dobbeltklikk for automatisk
          størrelse. Høyreklikk for å tilbakestille.
        </p>

        <h5>Omorganiser kolonner:</h5>
        <p>Klikk og dra en kolonneoverskrift for å omorganisere tabellen.</p>

        <h5>Kolonneverktøylinje:</h5>
        <p>
          På venstre side er det en meny med kolonneoversikt. Her kan du slå av og på enkeltkolonner eller grupper. Dra
          i listen for å omorganisere. <br /> Disse endringene vil kun påvirke din nåværende visning og vil ikke bli
          lagret.
        </p>

        <h5>Kolonnegruppering:</h5>
        <p>
          Kolonner har blitt gruppert med etiketter for enklere organisering. Tilpassede attributtkolonner vil
          automatisk bli gruppert etter seksjon.
        </p>

        <h5>Celleinteraksjon:</h5>
        <p>
          Dobbeltklikk på en celle for å redigere innholdet. Høyreklikk for å kopiere til utklippstavlen.
          <br /> Hvis teksten er for lang for en kolonne, klikk på pilen ned i høyre hjørne av cellen for å utvide. For
          å utvide alle kolonner, klikk på den blå knappen ved siden av fanene for tabellvisning.
        </p>
      </div>
    );
  };

  const ExpandAllDetailRowsButton = ({ api }) => {
    const [allRowsExpanded, setAllRowsExpanded] = useState<boolean>(false);

    const toggleAllRowsExpanded = () => {
      const expandAction = !allRowsExpanded;

      api.forEachNode((node) => {
        // only expand rows that should have detail
        if (node?.data?.taskOrIssue === "ISSUE") {
          node.setExpanded(expandAction);
        } else {
          // make sure TASK rows are collapsed
          node.setExpanded(false);
        }
      });

      setAllRowsExpanded(expandAction);
      api.refreshCells({ force: true });
    };
    return (
      <div>
        <div
          className={`expandIcon${allRowsExpanded === true ? " rotateIcon" : ""}`}
          onClick={() => toggleAllRowsExpanded()}
        >
          <div className="d-flex flex-column">
            <i className="fa fa-angle-down" style={{ marginBottom: "-5px" }}></i>
            <i className="fa fa-angle-down" style={{ marginTop: "-4px" }}></i>
          </div>
        </div>
      </div>
    );
  };
  const handleToolPanelVisibleChanged = (params) => {
    if (params.key === "columns" && params.visible === false) {
      localStorage.setItem("columnPanelSeen", "true");
    }
  };
  const icons = useMemo<{
    [key: string]: ((...args: any[]) => any) | string;
  }>(() => {
    return {
      "export-panel": '<span class="fa fa-file-excel-o fa-fw"></span>',
      info: '<span class="fa fa-question-circle"></span>',
    };
  }, []);
  const sideBar = useMemo(() => {
    const sb: SideBarDef = {
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivotMode: true,
            // Other parameters...
          },
        },
        {
          id: "info",
          labelDefault: "Info",
          labelKey: "info",
          iconKey: "info",
          toolPanel: InfoPanel,
        },
      ],
      position: "left",
      defaultToolPanel: localStorage.getItem("columnPanelSeen") === "true" ? "" : "columns",
    };
    return sb;
  }, []);

  const AgGridUserCellRenderer: React.FC<{ user: IUserDepricated }> = ({ user }) => {
    return user != null ? (
      <div className="d-flex align-items-center">
        <Avatar id={user._id} name={user.name} />
        <div className="ml-2">{user.name}</div>
      </div>
    ) : (
      <div></div>
    );
  };

  const columnTypes = useMemo(
    () => ({
      alwaysHidden: {
        hide: true,
        suppressColumnsToolPanel: true,
        suppressFiltersToolPanel: true,
      },
    }),
    [boardHasClientAccess],
  );

  const handleUpdateIssueField = useCallback(
    async (issueId: string, attr: string, value: any, oldValue: any = "") => {
      await updateIssue({
        projectId,
        issueBoardId,
        issueId,
        attr,
        value,
        oldValue,
      });
    },
    [projectId, issueBoardId, updateIssue],
  );

  const columns: ColDef<any>[] = useMemo(
    () => [
      {
        headerName: "",
        cellClass: "p-0",
        cellRenderer: (props) => {
          const { node, api, data } = props;

          // Only rows with detail should show an expander
          const isIssue = data?.taskOrIssue === "ISSUE";

          const handleClick = (e) => {
            e.preventDefault();
            e.stopPropagation();

            if (!isIssue) return;

            node.setExpanded(!node.expanded);
            api.refreshCells({ force: true });
            api.setFocusedCell(node.rowIndex, "id", null);
          };

          if (!isIssue) {
            // return empty space so row alignment stays consistent
            return <div style={{ width: 40 }} />;
          }

          return (
            <div
              style={{ marginLeft: "3px" }}
              onClick={handleClick}
              className={`expandIcon${node.expanded === true ? " rotateIcon" : ""}`}
            >
              <i className="fa fa-angle-down" />
            </div>
          );
        },
        width: 40,
        maxWidth: 40,
        suppressSizeToFit: true,
        suppressHeaderMenuButton: true,
        suppressColumnsToolPanel: true,
        suppressMovable: true,
        pinned: "left",
        context: ISSUE_SECTIONS.METACOLUMN,
        headerClass: "px-0 py-0",
        resizable: false,
        headerComponent: ExpandAllDetailRowsButton,
      },
      {
        field: "_id",
        headerName: "-",
        type: "alwaysHidden",
      },
      {
        field: "connecetedIssue",
        headerName: "-",
        type: "alwaysHidden",
      },
      {
        field: "connectedTasks",
        headerName: "-",
        type: "alwaysHidden",
      },
      {
        ...getAgGridIssueColTextAndLabel(ISSUE_DATAFILEDS_ENUM.ID),
        minWidth: 140,
        width: 140,
        cellClass: "grid-display-block px-1",
        // sort: "desc",
        cellRenderer: (params) => (
          <>
            <IssueIdWithAksjonCompletedCount
              id={params.value}
              issue={params.data}
              setSelectedIssue={setSelectedIssue}
              showClientLock={boardHasClientAccess && params.data?.clientAccess !== true}
            />
          </>
        ),
      },
      {
        ...getAgGridIssueColTextAndLabel(ISSUE_DATAFILEDS_ENUM.TITLE),
        minWidth: 200,
        filter: "agTextColumnFilter",
        maxWidth: 800,
        autoHeight: true,
        flex: 2,
        editable: true,
      },
      {
        ...getAgGridIssueColTextAndLabel(ISSUE_DATAFILEDS_ENUM.DESCRIPTION),
        autoHeight: true,
        flex: 2,
        minWidth: 200,
        maxWidth: 600,
        cellClass: "overflow-hidden",
        cellEditor: "agLargeTextCellEditor",
        cellEditorPopup: true,
        cellEditorParams: {
          maxLength: 5000,
        },
        filter: "agTextColumnFilter",
        cellRenderer: (params) => {
          return CellEditFormatter(params.value, ISSUE_DATAFILEDS_ENUM.DESCRIPTION);
        },
        editable: true,
      },
      {
        ...getAgGridIssueColTextAndLabel(ISSUE_DATAFILEDS_ENUM.CONCLUSION),
        autoHeight: true,
        flex: 2,
        minWidth: 200,
        maxWidth: 600,
        cellClass: "overflow-hidden",
        cellEditor: "agLargeTextCellEditor",
        cellEditorPopup: true,
        cellEditorParams: {
          maxLength: 5000,
        },
        filter: "agTextColumnFilter",
        cellRenderer: (params) => {
          return CellEditFormatter(params.value, ISSUE_DATAFILEDS_ENUM.CONCLUSION);
        },
        editable: true,
      },
      {
        ...getAgGridIssueColTextAndLabel(ISSUE_DATAFILEDS_ENUM.TYPEOFISSUE),
        minWidth: 80,
        cellEditor: "agRichSelectCellEditor", // Use the built-in SelectCellEditor
        editable: true,
        filter: "agSetColumnFilter",
        cellEditorParams: {
          values: types ?? [],
        },
      },
      {
        ...getAgGridIssueColTextAndLabel(ISSUE_DATAFILEDS_ENUM.PNS),
        cellRenderer: (params) => {
          const pnsItem = pnsItems?.find((pns) => pns.props?.pnsId === params.value);
          if (pnsItem != null) {
            return `${pnsItem.props?.pnsId} - ${pnsItem.props?.title}`;
          } else {
            return params.value;
          }
        },
      },
      {
        ...getAgGridIssueColTextAndLabel(ISSUE_DATAFILEDS_ENUM.CREATEDBY_NAME),
        cellRenderer: (params) => <AgGridUserCellRenderer user={params.data.createdBy} />,
        filter: "agSetColumnFilter",
        context: ISSUE_SECTIONS.HISTORY,
        editable: false,
        minWidth: 140,
      },
      {
        ...getAgGridIssueColTextAndLabel(ISSUE_DATAFILEDS_ENUM.ASSIGNEDTO_NAME),
        ...agUserCellEditor("assignedTo"),
        cellRenderer: (params) => <AgGridUserCellRenderer user={params.data.assignedTo} />,
        minWidth: 160,
      },
      {
        ...getAgGridIssueColTextAndLabel(ISSUE_DATAFILEDS_ENUM.CREATEDAT),
        width: 125,
        valueFormatter: dateExportFormatter,
        valueGetter: (params) => new Date(params.data.createdAt),
        cellRenderer: (params) => formatShortDate(params.value, true),
        filter: "agDateColumnFilter",
        context: ISSUE_SECTIONS.HISTORY,
        editable: false,
      },
      {
        ...getAgGridIssueColTextAndLabel(ISSUE_DATAFILEDS_ENUM.LASTUPDATEDBY_NAME),
        cellRenderer: (params) => <AgGridUserCellRenderer user={params.data.lastUpdatedBy} />,
        filter: "agSetColumnFilter",
        context: ISSUE_SECTIONS.HISTORY,
        editable: false,
      },
      {
        ...getAgGridIssueColTextAndLabel(ISSUE_DATAFILEDS_ENUM.UPDATEDAT),
        width: 125,
        valueFormatter: dateExportFormatter,
        valueGetter: (params) => new Date(params.data.updatedAt),
        filter: "agDateColumnFilter",
        cellRenderer: (params) => formatShortDate(params.value, true),
        context: ISSUE_SECTIONS.HISTORY,
        editable: false,
      },
      {
        ...getAgGridIssueColTextAndLabel(ISSUE_DATAFILEDS_ENUM.OWNER_NAME),
        ...agUserCellEditor("owner"),
        cellRenderer: (params) => <AgGridUserCellRenderer user={params.data.owner} />,
      },
      {
        ...getAgGridIssueColTextAndLabel(ISSUE_DATAFILEDS_ENUM.DATES_PLANNEDSTART),
        width: 125,
        ...agDateCellEditor("plannedStart"),
        filter: "agDateColumnFilter",
        cellRenderer: (params) => formatShortDate(params.value, true),
      },
      {
        ...getAgGridIssueColTextAndLabel(ISSUE_DATAFILEDS_ENUM.DATES_DUE),
        width: 127,
        filter: "agDateColumnFilter",
        ...agDateCellEditor("due"),

        cellRenderer: (params) => {
          if (isValidDateValue(params.value) === false) {
            return "-";
          }
          return (
            <DisplayDueDate
              dueDate={params.value}
              isComplete={
                params.data != null &&
                (params.data.status === ISSUE_COMPLETED_VALUE || params.data.status === ISSUE_EXPIRED)
              }
            />
          );
        },
      },
      {
        ...getAgGridIssueColTextAndLabel(ISSUE_DATAFILEDS_ENUM.DATES_COMPLETED),
        // minWidth: 125,
        ...agDateCellEditor("completed"),
        filter: "agDateColumnFilter",
        cellRenderer: (params) => formatShortDate(params.value, true),
      },
      {
        ...getAgGridIssueColTextAndLabel(ISSUE_DATAFILEDS_ENUM.TAGS),
        cellClass: "grid-display-block p-0",
        filter: "agSetColumnFilter",
        autoHeight: true,
        cellRenderer: (params) => (
          <Tags
            tags={params.value}
            updateCb={(tags: string[]) => handleUpdateIssueField(params.data._id, "tags", tags)}
            fetchUniqTags={doFetchUniqIssueTagsOnBoardDepricated}
            fetchArgs={[projectId, issueBoardId]}
          />
        ),
      },
      {
        ...getAgGridIssueColTextAndLabel(ISSUE_DATAFILEDS_ENUM.STATUS),
        editable: true,
        filter: "agSetColumnFilter",
        valueSetter: (params) => {
          return params.newValue;
        },
        cellEditor: "agRichSelectCellEditor",
        cellEditorParams: {
          values: ISSUE_STATES_OPTIONS.map((opt) => opt.value),
          cellRenderer: (params) => {
            return (
              <OptionBadge
                activeOption={params.value}
                options={convertValueAndColorToMultiDisplayColor(ISSUE_STATES_OPTIONS)}
                noWrap={true}
              />
            );
          },
          valueListMaxHeight: 220,
        },
        cellRenderer: (params) => {
          return (
            <OptionBadge
              activeOption={params.value}
              options={convertValueAndColorToMultiDisplayColor(ISSUE_STATES_OPTIONS)}
            />
          );
        },
      },
      {
        filter: "agSetColumnFilter",
        ...getAgGridIssueColTextAndLabel(ISSUE_DATAFILEDS_ENUM.DISCIPLIN),
      },
      {
        ...getAgGridIssueColTextAndLabel(ISSUE_DATAFILEDS_ENUM.ITEMMEMBERS),

        cellClass: "grid-display-block",
        valueFormatter: (params) => {
          if (params.value == null) {
            return "";
          }
          return params.value.map((user: IMinUserDepricated | IUserPartial) => user.name).join(", ");
        },
        valueGetter: (params) => params.data?.itemMembers?.map((user) => user.name),
        filter: "agSetColumnFilter",
        autoHeight: true,
        cellRenderer: (params) => {
          return (
            <McMantineMultiUserSelect
              displayVariant="tableCell"
              data={members
                .map((member) => member.user)
                .filter((user): user is IUserDepricated => user != null && typeof user !== "string")}
              defaultValue={params.data?.itemMembers ?? []}
              onChange={(selectedUsers) =>
                updateItemMembers(params.data._id, selectedUsers as unknown as IMinUserDepricated[])
              }
            />
          );
        },
      },
      {
        ...getAgGridIssueColTextAndLabel(ISSUE_DATAFILEDS_ENUM.PRIORITY),
        editable: true,
        filter: "agSetColumnFilter",
        filterParams: {
          valueFormatter: ({ value }) => ISSUE_PRIORTY_OPTIONS.find((opt) => opt.key === value)?.displayName || value,
        },
        valueSetter: (params) => {
          return params.newValue.key;
        },
        cellEditor: "agRichSelectCellEditor",
        cellEditorParams: {
          values: ISSUE_PRIORTY_OPTIONS.map((opt) => opt.key),
          cellRenderer: (params) => (
            <OptionBadge activeOption={params.value} options={ISSUE_PRIORTY_OPTIONS} noWrap={true} />
          ),
          valueListMaxHeight: 220,
        },
        cellRenderer: (params) => <OptionBadge activeOption={params.value} options={ISSUE_PRIORTY_OPTIONS} />,
      },
      {
        ...getAgGridIssueColTextAndLabel(ISSUE_DATAFILEDS_ENUM.REFERENCEURL),
        minWidth: 200,
        maxWidth: 400,
        cellEditor: "agLargeTextCellEditor",
        cellEditorPopup: true,
        cellEditorParams: {
          maxLength: 5000,
        },
        editable: true,
        autoHeight: true,
        cellRenderer: (params) => CellEditFormatter(params.value, ISSUE_DATAFILEDS_ENUM.REFERENCEURL),
        filter: "agTextColumnFilter",
      },

      {
        headerName: issueBoard?.bucketNameSetting,
        field: ISSUE_DATAFILEDS_ENUM.BUCKET,
        ...hideAndSort(ISSUE_DATAFILEDS_ENUM.BUCKET),
        cellClass: "overflow-hidden",
        cellRenderer: (params) => CellEditFormatter(params.value, ISSUE_DATAFILEDS_ENUM.BUCKET),
        maxWidth: 250,
        editable: true,
        filter: "agSetColumnFilter",
        cellEditor: "agRichSelectCellEditor",
        cellEditorParams: {
          values: boardBuckets.map((bucket) => bucket.value),
          valueListMaxHeight: 220,
        },
      },
      {
        ...getAgGridIssueColTextAndLabel(ISSUE_DATAFILEDS_ENUM.CONNECTEDBIMSPACES),
        autoHeight: true,
        minWidth: 280,
        flex: 2,
        filter: "agTextColumnFilter",
        valueGetter: (params) =>
          (params.data?.connectedBimSpaces ?? [])
            .map((space) => [space.roomNumber, space.roomName, space.floor, space.building].filter(Boolean).join(" "))
            .join(", "),
        cellClass: "grid-display-block p-0",
        cellRenderer: (params) => (
          <div onClick={(e) => e.stopPropagation()}>
            <IssueConnectedBimSpacesSection issue={params.data} projectId={projectId} hideTitle />
          </div>
        ),
      },
      {
        ...getAgGridIssueColTextAndLabel(ISSUE_DATAFILEDS_ENUM.HOURS_ORIGNALESTIMATE),
        valueFormatter: (params) => {
          return params.value != null ? params.value : 0;
        },
        editable: true,
        filter: "agNumberColumnFilter",
        context: ISSUE_SECTIONS.TIME,
      },
      {
        ...getAgGridIssueColTextAndLabel(ISSUE_DATAFILEDS_ENUM.HOURS_COMPLETED),
        editable: true,
        context: ISSUE_SECTIONS.TIME,
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => {
          return params.value != null ? params.value : 0;
        },
      },
      {
        ...getAgGridIssueColTextAndLabel(ISSUE_DATAFILEDS_ENUM.HOURS_REMAINING),
        editable: true,
        context: ISSUE_SECTIONS.TIME,
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => {
          return params.value != null ? params.value : 0;
        },
      },
    ],
    [],
  );
  const DetailCellRenderer = ({ data }: CustomCellRendererProps) => {
    const { data: tasks, isError } = useGetProjectByProjectIdConnectedTasksQuery({
      issueId: data._id,
      projectId: projectId,
    });

    const gridRef = useRef<HTMLDivElement>(null);

    const filterColumnsByField = (columns: any[], allowedFields: string[]): any[] => {
      return columns.filter((col) => allowedFields.includes(col?.field));
    };

    const disabledColumnsTasks = issueBoard?.disabledColumnsTasks ?? [];

    const columnsTasks: ColDef<any>[] = useMemo(() => {
      const columnsWithCopy = [
        ...filterColumnsByField(columns, [
          "id",
          "title",
          "assignedTo.name",
          "status",
          "dates.due",
          "dates.started",
          "dates.completed",
        ]).filter((col) => !disabledColumnsTasks.includes(col.field)),
      ];

      const createCopyColumn: ColDef<IIssueApi> = {
        field: "_id",
        headerName: "Lag kopi",
        width: 90,
        minWidth: 90,
        suppressMovable: true,
        resizable: false,
        sortable: false,
        cellClass: "grid-display-block",
        cellRenderer: (params: CustomCellRendererProps<IIssueApi>) => (
          <div className="">
            <CopyIssueButton issue={params.data} taskOrIssue={"TASK"} />
          </div>
        ),
      };

      const createDeleteColumn: ColDef<IIssueApi> = {
        field: "_id",
        headerName: "Slett",
        width: 72,
        minWidth: 72,
        suppressMovable: true,
        resizable: false,
        sortable: false,
        cellClass: "grid-display-block",
        cellRenderer: (params: CustomCellRendererProps<IIssueApi>) => (
          <ConnectedTaskDeleteCell
            task={params.data}
            projectId={projectId}
            taskDefiniteLabel={getIssueActionNameOption(issueBoard?.taskNameSetting, "definite") ?? "Aksjonen"}
            controlSize={32}
            iconSize={20}
          />
        ),
      };

      const createReorderColumn: ColDef<IIssueApi> = {
        headerName: "",
        width: 40,
        minWidth: 40,
        maxWidth: 40,
        rowDrag: true,
        suppressMovable: true,
        sortable: false,
        resizable: false,
        pinned: "left",
        cellClass: "ag-row-drag",
      };

      const otherAttributeTasksColumns = getAgGridOtherAttributeColumns(otherAttributesTasksConfig, true);

      return [
        createReorderColumn,
        ...columnsWithCopy,
        ...otherAttributeTasksColumns,
        createCopyColumn,
        ...(issueBoard?.disableDeleteIssue === true ? [] : [createDeleteColumn]),
      ];
    }, []);

    useEffect(() => {
      if (gridRef.current) {
        gridRef.current.style.visibility = "hidden";
      }
    }, []);

    const onRowDragEnd = async (event) => {
      if (!event) return;

      const newOrder = event.api.getRenderedNodes().map((node) => node.data._id);

      try {
        await reorderConnectedTasks({
          projectId,
          body: {
            issueId: data._id,
            newOrder,
          },
        }).unwrap();
      } catch (error) {
        console.error(error);
        dispatch(setShowFailedMessageThunk("Feil ved endring av rekkefølge på tilkoblede saker"));
      }
    };

    return (
      <Stack gap="xs" p="xs">
        {isError === true && <FailedAlert />}
        <Flex align="center" gap="xs">
          <Title order={6} fw={400}>
            {getIssueActionNameOption(issueBoard?.taskNameSetting, "plural")}
          </Title>
          <Button
            size="compact-xs"
            variant="outline"
            onClick={() =>
              addNewIssue({
                projectId: projectId,
                issueBoardId: issueBoardId,
                issueTitle: "",
                taskOrIssue: "TASK",
                issueIdIfItIsATask: data._id,
              })
            }
          >
            <IconPlus size={14} />
          </Button>
        </Flex>
        {data.connectedTasks?.length > 0 && (
          <div ref={gridRef}>
            <AgGridReact
              popupParent={document.querySelector("body") || undefined}
              rowHeight={45}
              defaultColDef={defaultColDef}
              domLayout={"autoHeight"}
              columnDefs={columnsTasks}
              getRowId={(p) => p.data._id}
              rowData={tasks}
              stopEditingWhenCellsLoseFocus={true}
              animateRows={false}
              readOnlyEdit={true}
              onCellEditRequest={onCellEditRequest}
              suppressContextMenu={true}
              suppressCutToClipboard={true}
              maintainColumnOrder={true}
              localeText={AG_GRID_LOCALE_NOR}
              onFirstDataRendered={() => {
                if (gridRef.current) {
                  gridRef.current.style.visibility = "visible";
                }
              }}
              rowDragManaged={true}
              onRowDragEnd={onRowDragEnd}
            />
          </div>
        )}
        {data.connectedTasksAndIssuesFromOtherBoards?.length > 0 && (
          <ConnectedTasksAndIssuesMiniTable
            fullWidth
            issue={data}
            issueId={data._id}
            issueBoardId={issueBoard._id}
            projectId={projectId}
            issueBoard={issueBoard}
          />
        )}
      </Stack>
    );
  };

  const getAgGridOtherAttributeColumns = (
    otherAttributesConfig: IOtherAttributeConfigApi,
    isTaskColumns?: boolean,
  ): ColDef<any>[] => {
    const otherAttributeColumns: ColDef<any>[] = [];
    const otherAttributesEntries = Object.entries(otherAttributesConfig ?? {}).map(([key, value]) => ({
      key: key,
      ...value,
    }));

    // Group by the 'section' key
    const groupedBySection = R.groupBy((item) => item.section, otherAttributesEntries);
    Object.entries(groupedBySection).map(([sectionTitle, attributesInSection]) => {
      attributesInSection
        .sort((a, b) => a.order - b.order)
        .forEach((attribute) => {
          if (attribute.isActive !== true) {
            return;
          }
          let customParams: {};
          switch (attribute.typeOfValue) {
            case "MULTILINESTRING":
              customParams = {
                filter: "agTextColumnFilter",
                cellEditor: "agLargeTextCellEditor",
                cellEditorPopup: true,
                minWidth: 200,
                cellRenderer: (params) => CellEditFormatter(params.value),
                autoHeight: true,
              };
              break;
            case "NUMBER":
              customParams = {
                filter: "agNumberColumnFilter",
                cellEditor: "agNumberCellEditor",

                valueGetter: (params) => {
                  const val = params.data.otherAttributesData?.[attribute.key];
                  if (Array.isArray(val) || val == null || val === "") return null;
                  const num = Number(val);
                  return Number.isNaN(num) ? null : num;
                },

                // ✅ format for display
                valueFormatter: (p) => formatNumberNb(p.value),

                valueSetter: (params) => {
                  const newValue = params.newValue;
                  if (Array.isArray(newValue)) return false;

                  // allow user to type "1 000 000" or "1000000"
                  const cleaned = String(newValue)
                    .replace(/[\s\u00A0\u202F]/g, "")
                    .replace(",", ".");
                  const num = Number(cleaned);

                  if (Number.isNaN(num)) return false;

                  params.data.otherAttributesData = {
                    ...params.data.otherAttributesData,
                    [attribute.key]: num,
                  };
                  return true;
                },
              };
              break;
            case "BOOLEAN":
              customParams = {
                filter: "agFilter",
                editable: false,
                valueGetter: (params) =>
                  params.data.otherAttributesData?.[attribute.key] === true
                    ? "Ja"
                    : params.data.otherAttributesData?.[attribute.key] === false
                    ? "Nei"
                    : "", // norwegian values not shown, but needed for export
                cellRenderer: (params) => (
                  <div className="d-flex align-items-center h-100">
                    <SimpleStampCheck
                      disabled={attribute.locked === true}
                      toggled={params.value == "" ? null : params.value === "Ja"}
                      toggle={(val) => handleUpdateIssueField(params.data._id, params.colDef.field, val)}
                    />
                  </div>
                ),
              };

              break;
            case "DROPDOWN":
              const options = otherAttributesConfig?.[attribute.key]?.tableOptions ?? [];
              const isNumberDropdown = otherAttributesConfig?.[attribute.key]?.dropdownOptionType === "NUMBER";
              const OptionWithColor = (params: ValueFormatterParams) => {
                const values = Array.isArray(params.value) ? params.value : [params.value];

                return (
                  <div className="d-flex flex-wrap">
                    {values.map((value, index) => {
                      const foundOption = options.find((o) => String(o.value) === String(value));
                      return (
                        <div key={index} className="d-flex align-items-center mr-2">
                          {foundOption?.color && (
                            <div className="statusdot" style={{ backgroundColor: foundOption?.color }}></div>
                          )}
                          <div>
                            {isNumberDropdown && Number.isFinite(Number(value)) ? (
                              <NumberFormatter value={Number(value)} thousandSeparator=" " decimalSeparator="," />
                            ) : (
                              String(value ?? "")
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              };
              const formatDropdownValue = (value: unknown) => {
                if (Array.isArray(value)) {
                  return value.map((item) => (isNumberDropdown ? formatNumberNb(item) : String(item ?? ""))).join(", ");
                }

                return isNumberDropdown ? formatNumberNb(value) : String(value ?? "");
              };
              customParams = {
                filter: "agFilter",
                cellEditor: "agRichSelectCellEditor",
                editable: attribute.locked !== true,
                valueFormatter: (params) => formatDropdownValue(params.value),
                valueSetter: (params) => {
                  return params.newValue;
                },
                cellEditorParams: {
                  values: options.map((opt) => opt.value),
                  cellRenderer: OptionWithColor,
                  valueListMaxHeight: 220,
                  multiSelect: otherAttributesConfig?.[attribute.key]?.allowMultipleSelectionsInDropdown === true,
                },
                cellRenderer: OptionWithColor,
              };
              break;
            case "DATE":
              customParams = {
                filter: "agDateColumnFilter",
                cellEditor: "agDateStringCellEditor",
                valueFormatter: dateExportFormatter,
                valueGetter: (params) => {
                  return params.data.otherAttributesData?.[attribute?.key] ?? "";
                },
              };
              break;
            case "FORMULA":
              customParams = {
                editable: false,
                filter: "agTextColumnFilter",
                minWidth: 200,
              };
              break;
            case "MATH_FORMULA":
              customParams = {
                editable: false,
                filter: "agTextColumnFilter",
                minWidth: 200,
                valueFormatter: (params) => formatNumberNb(params.value),
              };
              break;
            default:
              customParams = { filter: "agTextColumnFilter" };
              break;
          }

          const customAttributeHeaderTemplate = (
            color: string,
            isLocked: boolean,
            isFormula: boolean,
            isMathFormula: boolean,
          ) => {
            return `<div class="ag-cell-label-container py-0" role="presentation">
                      <span data-ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
                      <span data-ref="eFilterButton" class="ag-header-icon ag-header-cell-filter-button"></span>
                      <div data-ref="eLabel" class="ag-header-cell-label" role="presentation">
                        <span data-ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>
                        <span data-ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>
                        <span data-ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>
                        <span data-ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
                        ${isLocked === true ? `<div class="mr-1"><i class="fa fa-lock"></i></div>` : ""}
                        ${
                          color != null && color.length > 0
                            ? `<div class="mmi-dot mr-1" style="background-color: ${color};"></div>`
                            : ""
                        }
                        ${
                          isFormula
                            ? `<div class="mr-1 text-muted" title="Formelfelt" style="font-weight: 600; font-size: 12px;">ƒx</div>`
                            : ""
                        }
                        ${
                          isMathFormula
                            ? `<div class="mr-1" title="Beta-funksjon" style="font-weight: 700; font-size: 10px; color: #8a6d3b; background: #fcf8e3; border: 1px solid #faebcc; border-radius: 10px; padding: 0 6px; line-height: 16px;">BETA</div>`
                            : ""
                        }
                        <span data-ref="eText" class="ag-header-cell-text three-line-text-truncate" role="columnheader"></span>
                        <div class="d-flex align-items-baseline" style="flex-direction: row;"></div>
                        <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
                      </div>
                    </div>`;
          };

          const otherAttributeColumn: ColDef = {
            field: `otherAttributesData.${attribute.key}`,
            headerName: attribute.label,
            autoHeaderHeight: true,
            // wrapHeaderText: true,
            headerComponentParams: {
              template: customAttributeHeaderTemplate(
                attribute.labelColor,
                attribute.locked,
                attribute.typeOfValue === "FORMULA" || attribute.typeOfValue === "MATH_FORMULA",
                attribute.typeOfValue === "MATH_FORMULA",
              ),
            },
            editable: attribute.locked !== true,
            ...customParams,
            maxWidth: 300,
            context: sectionTitle,
            cellClass: "overflow-hidden px-1",
            ...(isTaskColumns !== true ? hideAndSort(`otherAttributesData.${attribute.key}`) : {}),
          };
          otherAttributeColumns.push(otherAttributeColumn);
        });
    });
    return otherAttributeColumns;
  };

  const getRisikoEvaluationColumns = (issueBoard: IIssueBoardApi): ColDef<any>[] => {
    const categoriesFromBoard: IMapOfEvaluationCategoriesApi =
      issueBoard?.risikoSection?.evaluationCategoriesConfig ?? {};
    const evaluationCategories = Object.entries(categoriesFromBoard)
      .filter(([key, value]) => value.isActive)
      .map(([key, value]) => ({
        key: key,
        ...value,
      }));
    const probabilityColumn: ColDef<any> = {
      ...getAgGridIssueColTextAndLabel(ISSUE_DATAFILEDS_ENUM.RISK_PROBABILITY),
      editable: true,
      context: ISSUE_SECTIONS.RISIKO,
      valueFormatter: (params) => {
        const risikoItem = issueBoard?.risikoSection?.probabilites[params.value];
        return risikoItem?.name || "";
      },
      filter: "agFilter",
      cellEditor: "agRichSelectCellEditor",
      cellEditorParams: {
        values: Object.keys(issueBoard?.risikoSection?.probabilites || {}),
        valueFormatter: (params) => {
          const risikoItem = issueBoard?.risikoSection?.probabilites?.[params.value];
          return risikoItem?.name || "";
        },
      },
    };

    const evaluationColumns: ColDef<any>[] = evaluationCategories.flatMap((category) => {
      const consequenceColDef: ColDef<any> = {
        field: `risiko.consequence.${category.key}`,
        headerName: `${category?.name} konsekvens`,
        headerClass: "px-2 py-0 two-line-header",
        filter: "agFilter",
        editable: true,
        cellEditor: "agRichSelectCellEditor",
        valueFormatter: (params: ValueFormatterParams<IIssueApi>) => {
          const risikoItem: IRisikoProbability = categoriesFromBoard?.[category.key]?.consequences[params.value];
          return risikoItem?.name || "";
        },
        cellEditorParams: {
          values:
            Object.entries(categoriesFromBoard[category.key]?.consequences ?? {})
              .filter(([, obj]) => obj.isActive)
              .map(([id]) => id) ?? [],
          valueFormatter: (params) => {
            const risikoItem = categoriesFromBoard?.[category.key]?.consequences?.[params.value];
            return risikoItem?.name || "";
          },
        },
        context: ISSUE_SECTIONS.RISIKO,
        ...hideAndSort(`risiko.consequence.${category.key}`),
      };

      const matrixValueColDef: ColDef<IIssueApi> = {
        field: `risikoMatrixValue.${category.key}` as ColDefField<IIssueApi>,
        headerName: `${category.name} poeng`,
        headerClass: "px-2 py-0 two-line-header",
        wrapHeaderText: true,
        initialWidth: 100,
        filter: "agNumberColumnFilter",

        valueGetter: (params) => {
          try {
            const matrixItemAndScore = getMatrixItemAndScore({
              issue: params.data,
              issueBoard,
              evaluationCategoryCapital: category.key,
            });
            return matrixItemAndScore?.score;
          } catch (error) {
            return 0;
          }
        },
        cellRenderer: (params: ICellRendererParams<IIssueApi>) => {
          try {
            const matrixItemAndScore = getMatrixItemAndScore({
              issue: params.data,
              issueBoard: issueBoard,
              evaluationCategoryCapital: category.key,
            });

            return (
              <div
                className="d-flex flex-items-center justify-content-center"
                style={{
                  color: matrixItemAndScore?.matrixItem?.color != null ? "white" : "black",
                  backgroundColor: matrixItemAndScore?.matrixItem?.color,
                  fontSize: "12px",
                  borderRadius: "5px",
                  width: "100%",
                }}
              >
                {matrixItemAndScore?.score}
              </div>
            );
          } catch (error) {
            return <div>-</div>;
          }
        },
        context: ISSUE_SECTIONS.RISIKO,
        ...hideAndSort(`risikoMatrixValue.${category.key}`),
      };

      return [consequenceColDef, matrixValueColDef];
    });

    const categoryCommentCol: ColDef<IIssueApi> = {
      field: `risiko.categoryComment`,
      headerName: "Risiko kommentar",
      headerClass: "px-2 py-0 two-line-header",
      wrapHeaderText: true,
      initialWidth: 180,
      minWidth: 120,
      filter: "agTextColumnFilter",
      editable: true,
      context: ISSUE_SECTIONS.RISIKO,
      ...hideAndSort(`risiko.categoryComment`),
    };
    const pointTotalCol: ColDef<any> = {
      field: "risiko.pointTotal",
      headerName: "Total poengsum",
      headerClass: "px-2 py-0 two-line-header",
      wrapHeaderText: true,
      filter: "agNumberColumnFilter",
      context: ISSUE_SECTIONS.RISIKO,
      ...hideAndSort(`risiko.pointTotal`),
      valueGetter: (params) => {
        try {
          const totalPoints = evaluationCategories.reduce((sum, category) => {
            const value = Number(params.getValue(`risikoMatrixValue.${category.key}`));
            return sum + (value ? Number(value) : 0);
          }, 0);
          return totalPoints;
        } catch (error) {
          return 0;
        }
      },
    };
    const lowestPointCol: ColDef<any> = {
      field: "risiko.worstScore",
      headerName: "Verste kategori",
      headerClass: "px-2 py-0 two-line-header",
      filter: "agNumberColumnFilter",
      wrapHeaderText: true,
      context: ISSUE_SECTIONS.RISIKO,
      ...hideAndSort(`risiko.worstScore`),
      valueGetter: (params) => {
        try {
          const highestPoint = evaluationCategories.reduce((max, category) => {
            const value = Number(params.getValue(`risikoMatrixValue.${category.key}`));
            return Math.max(max, value || 0);
          }, 0);
          return highestPoint;
        } catch (error) {
          return 0;
        }
      },
    };

    return [probabilityColumn, ...evaluationColumns, pointTotalCol, lowestPointCol, categoryCommentCol];
  };

  const allColumns = [
    ...columns,
    ...getAgGridOtherAttributeColumns(otherAttributesConfig),
    ...(issueBoard?.risikoSection?.isActive ? getRisikoEvaluationColumns(issueBoard) : []),
  ];

  const groupedColumns: ColDef<any>[] = useMemo(() => {
    const visibleColumns = allColumns.map((col) => {
      return {
        ...col,
        hide: col.context === ISSUE_SECTIONS.METACOLUMN ? col.hide : !columnsVisibility.includes(col.field),
      };
    });
    // Existing column definitions remain unchanged
    const initialColumns = [
      {
        headerName: "",
        children: [...visibleColumns.filter((col) => col.context === ISSUE_SECTIONS.METACOLUMN)],
        suppressColumnsToolPanel: true,
      },
      {
        headerName: ISSUE_SECTIONS.COMMON,
        children: [...visibleColumns.filter((col) => col?.context === undefined && col.headerName.length > 0)],
      },
      {
        headerName: ISSUE_SECTIONS.TIME,
        children: [...visibleColumns.filter((col) => col?.context === ISSUE_SECTIONS.TIME)],
      },
      {
        headerName: ISSUE_SECTIONS.DATES,
        children: [...visibleColumns.filter((col) => col?.context === ISSUE_SECTIONS.DATES)],
      },
      {
        headerName: ISSUE_SECTIONS.HISTORY,
        children: [...visibleColumns.filter((col) => col?.context === ISSUE_SECTIONS.HISTORY)],
      },
      ...(issueBoard?.risikoSection?.isActive === true
        ? [
            {
              headerName: ISSUE_SECTIONS.RISIKO,
              children: [...visibleColumns.filter((col) => col?.context === ISSUE_SECTIONS.RISIKO)],
            },
          ]
        : []),
    ];

    // Get custom attribute columns and group them by type
    const otherAttributeColumns = getAgGridOtherAttributeColumns(otherAttributesConfig);
    const groupedByType = R.groupBy((colDef) => colDef.context.toString(), otherAttributeColumns);
    const otherAttributeColumnGroups = Object.entries(groupedByType).map(([type, cols]) => ({
      headerName: type,
      children: cols,
    }));
    return [...initialColumns, ...otherAttributeColumnGroups];
    // }, [generatedFormulas]);
  }, []);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      minWidth: 100,
      suppressMovable: false,
      resizable: true,
      suppressHeaderMenuButton: true,
      editable: false,
      // wrapText: true,
      //autoHeaderHeight: true, // this causes crash in chrome update 2025-16-01
      cellClass: "p-1",
      headerClass: "px-2 py-1",
      cellStyle: {
        whiteSpace: "normal",
        lineHeight: "1.5",
        wordBreak: "break-word",
      },
    }),
    [],
  );
  const handleColumnStateUpdate = (e) => {
    const columnState =
      e.api.getAllDisplayedColumns()?.map((col, index) => ({
        colId: col.getColId(),
        orderPriority: index,
        width: col.getActualWidth(),
      })) ?? [];

    dispatch(updateColumnStateThunk(columnState));
  };
  const debouncedHandleToggleColumnVisibility = useCallback(
    debounce((e) => {
      handleColumnStateUpdate(e);
    }, 1000), //lets animation finish, saves in background
    [dispatch],
  );
  const onSortChanged = (params) => {
    const updatedColumn = params.api.getColumnState().filter((s) => s.sort !== null)[0];
    const formattedColumn = updatedColumn
      ? { column: updatedColumn.colId, direction: updatedColumn.sort.toUpperCase() }
      : null;
    dispatch(setIssueSortedColInRedux(formattedColumn));
  };
  const { onDragStarted, onDragStopped } = useDragColumnChange((e) => handleColumnStateUpdate(e));
  const onColumnResized = useCallback(
    (event) => {
      if (event.finished !== true) return;
      handleColumnStateUpdate(event);
    },
    [dispatch],
  );

  const onCellEditRequest = (event: CellEditRequestEvent<IIssueApi>) => {
    const issue = event.data;
    let updateValue: Date | string;
    if (event.colDef.field.startsWith("status")) {
      updateValue = event.newValue;
    } else if (event.colDef.field.startsWith("priority")) {
      updateValue = event.newValue;
    } else if (event.colDef.field.startsWith("dates")) {
      if (event.newValue == null) {
        updateValue = "";
      } else {
        const newDate = parseNorwegianDate(event.newValue);
        const oldDate = parseNorwegianDate(event.oldValue);

        if (newDate && oldDate && newDate.getTime() === oldDate.getTime()) {
          return;
        }

        updateValue = newDate;
      }
    } else {
      updateValue = event.newValue;
    }

    if (
      event.colDef.field === ISSUE_DATAFILEDS_ENUM.TITLE ||
      event.colDef.field === ISSUE_DATAFILEDS_ENUM.DESCRIPTION ||
      event.colDef.field === ISSUE_DATAFILEDS_ENUM.CONCLUSION ||
      event.colDef.field === ISSUE_DATAFILEDS_ENUM.REFERENCEURL
    ) {
      updateIssueSimple({
        projectId,
        body: {
          _id: issue._id,
          property: {
            [event.colDef.field]: updateValue,
          },
        },
      });
      return;
    }

    updateIssue({
      projectId: projectId,
      issueId: issue._id,
      attr: event.colDef.field,
      value: updateValue,
      oldValue: getLogValueFromCellValue(event.oldValue),
      issueBoardId: issueBoardId,
    });
  };

  return (
    <div className="d-flex flex-column w-100 h-100">
      <Modal
        opened={clickedRoomNumberInModal != null}
        onClose={closeRoomViewerModal}
        title="Romviewer"
        size="80vw"
        centered
        styles={{
          content: {
            maxWidth: "72rem",
          },
        }}
      >
        <LocalErrorBoundary id="IssueTableRoomViewerModal">
          <IssueModalRoomViewer />
        </LocalErrorBoundary>
      </Modal>

      <div className=" ag-theme-quartz h-100" id="myGrid" ref={refContainer}>
        <AgGridReact
          onDragStarted={onDragStarted}
          onDragStopped={onDragStopped}
          onColumnVisible={debouncedHandleToggleColumnVisibility}
          onColumnResized={onColumnResized}
          suppressHeaderFocus={true}
          cellSelection={true}
          rowHeight={55}
          headerHeight={40}
          columnTypes={columnTypes}
          maintainColumnOrder={true}
          ref={gridRef}
          getRowId={(p) => p.data._id}
          rowData={items}
          columnDefs={groupedColumns}
          stopEditingWhenCellsLoseFocus={true}
          readOnlyEdit={true}
          onCellEditRequest={onCellEditRequest}
          defaultColDef={defaultColDef}
          animateRows={false}
          icons={icons}
          onGridReady={(e) => {
            allColumnState[issueBoardId]?.forEach((col) => {
              if (col?.orderPriority != null) {
                e.api.moveColumns([col.colId], col.orderPriority);
              }
              if (col?.width != null) {
                e.api.setColumnWidths([{ key: col.colId, newWidth: col.width }]);
              }
            });
            // const autoSizeCols =
            //   e.api
            //     .getColumns()
            //     ?.filter((c) => !["title", "description", "conclusion", "id"].includes(c.getColId()))
            //     .map((c) => c.getColId()) ?? [];

            // e.api.autoSizeColumns(autoSizeCols, false);
          }}
          onFirstDataRendered={(e) => {
            // manually sets width of ID column for connected tasks icon
            const hasAnyTask = items?.some((i) => i.connectedTasks?.length > 0) ?? false;
            const idColumnConfig = allColumnState[issueBoardId]?.find((col) => col.colId === ISSUE_DATAFILEDS_ENUM.ID);
            if (idColumnConfig?.width == null) {
              e.api.setColumnWidths([{ key: ISSUE_DATAFILEDS_ENUM.ID, newWidth: hasAnyTask ? 140 : 100 }]);
            }
          }}
          sideBar={sideBar}
          pagination={true}
          paginationPageSize={500} // standard = 500
          paginationPageSizeSelector={[500, 1000]} // användaren kan välja 500 / 1000
          suppressColumnVirtualisation={columnsVisibility.length < 25}
          suppressRowVirtualisation={items?.length < 100}
          rowBuffer={20}
          masterDetail={true}
          detailRowAutoHeight={true}
          isRowMaster={(data) => data?.taskOrIssue === "ISSUE"}
          detailCellRenderer={DetailCellRenderer}
          localeText={AG_GRID_LOCALE_NOR}
          onToolPanelVisibleChanged={handleToolPanelVisibleChanged}
          onSortChanged={(e) => onSortChanged(e)}
          suppressCutToClipboard={true}
        />
      </div>
    </div>
  );
};

export default IssueTableAgGrid;
